import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "./BlogHero.module.css";
import { LanguageContext } from "../LanguageContext/LanguageContext.jsx";
import { FaHeart } from 'react-icons/fa';
import { useFilter } from '../FilterContext/FilterContext';

const marketingBackendUrl = import.meta.env.VITE_MARKETING_BACKEND_URL;

const BlogHero = ({ searchTerm }) => {
  const { selectedCategories } = useFilter();
  const [allBlogs, setAllBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleBlogs, setVisibleBlogs] = useState(4);
  const { direction } = useContext(LanguageContext);
  const navigate = useNavigate();
  const [wishlistStatus, setWishlistStatus] = useState({});
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const contentRef = useRef(null);
  const [notification, setNotification] = useState({ show: false, message: '' });

  const filterAndSortBlogs = useCallback((blogs) => {
    let filtered = [...blogs];
    
    if (selectedCategories.length > 0) {
      filtered = blogs.filter(blog => 
        selectedCategories.includes(blog.category)
      );
    }

    // Filter by search term across all content
    if (searchTerm) {
      filtered = filtered.filter(blog => 
        JSON.stringify(blog).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered.sort((a, b) => (a.rank || 0) - (b.rank || 0));
  }, [selectedCategories, searchTerm]);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${marketingBackendUrl}/maflam/blogs/light`);
        if (response.data.success) {
          setAllBlogs(response.data.data);
          setFilteredBlogs(response.data.data);
        }
      } catch (err) {
        console.error('Error fetching blogs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  useEffect(() => {
    const filtered = filterAndSortBlogs(allBlogs);
    setFilteredBlogs(filtered);
  }, [selectedCategories, allBlogs, filterAndSortBlogs]);

  useEffect(() => {
    const checkWishlistStatus = async () => {
      try {
        const userEmail = localStorage.getItem("useremailidis");
        if (!userEmail || !filteredBlogs.length) return;

        const response = await axios.get(`${marketingBackendUrl}/maflam/blog-wishlist/${userEmail}`);
        
        if (response.data.success) {
          const wishlistBlogs = response.data.data;
          const statusMap = {};
          
          filteredBlogs.forEach(blog => {
            statusMap[blog._id] = wishlistBlogs.some(
              wishlistBlog => wishlistBlog.headingEnglish === blog.heading
            );
          });
          
          setWishlistStatus(statusMap);
        }
      } catch (error) {
        console.error('Error checking wishlist status:', error);
      }
    };

    if (filteredBlogs.length > 0) {
      checkWishlistStatus();
    }
  }, [filteredBlogs]);

  useEffect(() => {
    if (contentRef.current) {
      const images = contentRef.current.getElementsByTagName('img');
      let loadedImages = 0;
      const totalImages = images.length;

      console.log('Total images to load:', totalImages);

      const imageLoaded = () => {
        loadedImages++;
        console.log(`Image loaded: ${loadedImages}/${totalImages}`);
        if (loadedImages === totalImages) {
          setImagesLoaded(true);
          console.log('All images loaded');
        }
      };

      if (totalImages === 0) {
        setImagesLoaded(true);
        return;
      }

      Array.from(images).forEach(img => {
        if (img.complete) {
          imageLoaded();
        } else {
          img.addEventListener('load', imageLoaded);
          img.addEventListener('error', imageLoaded);
        }
      });

      return () => {
        Array.from(images).forEach(img => {
          img.removeEventListener('load', imageLoaded);
          img.removeEventListener('error', imageLoaded);
        });
      };
    }
  }, [filteredBlogs]);

  useEffect(() => {
    const checkLoading = () => {
      if (allBlogs.length > 0 && imagesLoaded && document.readyState === 'complete') {
        setTimeout(() => {
          setLoading(false);
          console.log('Loading complete');
        }, 1000);
      }
    };

    checkLoading();

    const fallbackTimer = setTimeout(() => {
      if (loading) {
        console.log('Fallback: Force stopping loader');
        setLoading(false);
      }
    }, 5000);

    return () => clearTimeout(fallbackTimer);
  }, [allBlogs, imagesLoaded]);

  const handleContinueReading = (id) => {
    navigate(`/home/blogDetails/${id}`);
  };

  const handleLoadMore = () => {
    if (visibleBlogs >= filteredBlogs.length) {
      setVisibleBlogs(4); // Collapse to initial state
    } else {
      setVisibleBlogs((prev) => prev + 3); // Increase visible blogs by 3
    }
  };

  const handleWishlistToggle = async (blog) => {
    try {
      const userEmail = localStorage.getItem("useremailidis");
      
      if (!userEmail) {
        navigate("/home/login");
        return;
      }

      const isInWishlist = wishlistStatus[blog._id];

      if (isInWishlist) {
        // Remove from wishlist
        const response = await axios.post(`${marketingBackendUrl}/maflam/blog-wishlist/remove-blog`, {
          userEmail,
          headingEnglish: blog.heading
        });

        if (response.data.success) {
          setWishlistStatus(prev => ({
            ...prev,
            [blog._id]: false
          }));
          setNotification({
            show: true,
            message: direction === "rtl" 
              ? 'تمت إزالة المقال من قائمة الرغبات بنجاح'
              : 'Blog removed from wishlist successfully'
          });
          setTimeout(() => {
            setNotification({ show: false, message: '' });
          }, 2000);
        }
      } else {
        // Add to wishlist
        const blogDetails = {
          headingEnglish: blog.heading,
          headingArabic: blog.heading,
          subheadingEnglish: blog.subheading,
          subheadingArabic: blog.subheading,
          thumbnail: blog.bannerImage,
          redirectionLink: `/home/blogDetails/${blog._id}`
        };

        const response = await axios.post(`${marketingBackendUrl}/maflam/blog-wishlist`, {
          userEmail,
          blogDetails
        });

        if (response.data.success) {
          setWishlistStatus(prev => ({
            ...prev,
            [blog._id]: true
          }));
          setNotification({
            show: true,
            message: direction === "rtl" 
              ? 'تمت إضافة المقال إلى قائمة الرغبات بنجاح'
              : 'Blog added to wishlist successfully'
          });
          setTimeout(() => {
            setNotification({ show: false, message: '' });
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
      if (error.response?.status === 400) {
        setNotification({
          show: true,
          message: direction === "rtl"
            ? 'المقال موجود بالفعل في قائمة الرغبات'
            : 'Blog already exists in wishlist'
        });
      } else {
        setNotification({
          show: true,
          message: direction === "rtl"
            ? 'فشل في تحديث قائمة الرغبات'
            : 'Failed to update wishlist'
        });
      }
    }
  };

  if (loading) {
    return (
      <div className={styles.loaderWrapper}>
        <span className={styles.loader}></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.messageContainer}>
        <h2 className={styles.sectionHeading}>المقالات</h2>
        <p className={styles.errorMessage}>{error}</p>
      </div>
    );
  }

  if (filteredBlogs.length === 0) return (
    <div className={styles.messageContainer}>
      <h2 className={styles.sectionHeading}>المقالات</h2>
      <p className={styles.noItemsMessage}>لا توجد مقالات متاحة في هذه الفئة</p>
    </div>
  );

  return (
    <div ref={contentRef} style={{ direction: "rtl" }} className={styles.blogDetails}>
      {notification.show && (
        <div className={styles.notification}>
          {notification.message}
        </div>
      )}
      <h2 className={styles.sectionHeading}>المقالات</h2>
      <div className={styles.heroSection}>
        {filteredBlogs.length > 0 && (
          <div className={styles.featuredBlog}>
            <button 
              className={`${styles.wishlistButton} ${wishlistStatus[filteredBlogs[0]._id] ? styles.inWishlist : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                handleWishlistToggle(filteredBlogs[0]);
              }}
            >
              <FaHeart 
                style={{ 
                  height: '20px', 
                  width: '20px',
                  fill: wishlistStatus[filteredBlogs[0]._id] ? 'rgb(57, 255, 251)' : 'white'
                }} 
              />
            </button>
            <div className={styles.featuredContent}>
              <img
                src={filteredBlogs[0].bannerImage}
                alt="Featured Blog"
                className={styles.featuredImage}
              />
              <h2 className={styles.featuredTitle}>{filteredBlogs[0].heading}</h2>
             
              <button
                onClick={() => navigate(`/home/blogDetails/${filteredBlogs[0]._id}`)}
                className={styles.readMoreBtn}
              >
              اقرأ المزيد
                <span style={{ color: "white", fontWeight: "700", display: "inline-block" }}>
                  <svg xmlns="https://www.w3.org/2000/svg" width="15" height="12" viewBox="0 0 15 12" fill="none">
                    <path d="M14 6L1.14286 6M1.14286 6L6.14286 11M1.14286 6L6.14286 0.999999" stroke="#F7FFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </button>
            </div>
          </div>
        )}

        <div className={styles.blogList}>
          {filteredBlogs.slice(1, visibleBlogs).map((post) => (
            <div key={post._id} className={styles.blogCard}>
              <button 
                className={`${styles.wishlistButton} ${wishlistStatus[post._id] ? styles.inWishlist : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  handleWishlistToggle(post);
                }}
              >
                <FaHeart 
                  style={{ 
                    height: '20px', 
                    width: '20px',
                    fill: wishlistStatus[post._id] ? 'rgb(57, 255, 251)' : 'white'
                  }} 
                />
              </button>
              <div className={styles.blogImageContainer}>
                <img
                  src={post.bannerImage || "default-image-url.jpg"}
                  alt={post.heading}
                  className={styles.blogImage}
                />
              </div>
              <div className={styles.blogContent}>
                <h3 className={styles.title}>{post.heading}</h3>
          
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/home/blogDetails/${post._id}`);
                  }}
                  className={styles.readMore}
                >
                اقرأ المزيد
                  <span style={{ color: "white", fontWeight: "700", marginLeft: "10px", marginRight: "10px", display: "inline-block" }}>
                    <svg xmlns="https://www.w3.org/2000/svg" width="15" height="12" viewBox="0 0 15 12" fill="none">
                      <path d="M14 6L1.14286 6M1.14286 6L6.14286 11M1.14286 6L6.14286 0.999999" stroke="#F7FFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </a>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex",minWidth: "100%" , justifyContent: "center", alignItems: "center"}} className={styles.loadMoreBtn}>
        <button onClick={handleLoadMore} className={styles.loadMoreBtn}
                  style={{
                    backgroundColor: "#39FFFB",
                    color: "black",
                    height: "64px",
                    borderRadius: "32px",
                    fontSize: "17px",
                    fontWeight: "700",
                    paddingLeft: "30px",
                    paddingRight: "30px",
                    margin:"auto"
                  }}
        >
          {visibleBlogs >= filteredBlogs.length ? 'عرض أقل' : 'تحميل المزيد'}
        </button>
        </div>
      </div>
    </div>
  );
};

export default BlogHero;